export interface DBCSignal {
  name: string;
  canId: number;
  messageName: string;
}

export interface DuplicateSignalInfo {
  signalName: string;
  appearsIn: {
    id: number;
    name: string;
  }[];
}

export interface DBCParseResult {
  signals: DBCSignal[];
  duplicates: DuplicateSignalInfo[];
  hasDuplicates: boolean;
}

export interface ParseOptions {
  caseSensitive?: boolean;
  normalizeUnderscores?: boolean;
}

/**
 * Normalizes a signal name according to the specified options
 * @param signalName - The signal name to normalize
 * @param options - Normalization options
 * @returns Normalized signal name
 */
function normalizeSignalName(
  signalName: string,
  options: ParseOptions = {}
): string {
  let normalized = signalName;

  if (!options.caseSensitive) {
    normalized = normalized.toLowerCase();
  }

  if (options.normalizeUnderscores) {
    normalized = normalized.replace(/_/g, "");
  }

  return normalized;
}

/**
 * Parses a DBC file content to extract signal information and detect duplicates
 * following strict DBC parsing rules
 * @param dbcContent - The content of the DBC file as string
 * @param options - Parsing options for duplicate detection
 * @returns Parsed result with signals and duplicate information
 */
export function parseDBCFile(
  dbcContent: string,
  options: ParseOptions = {}
): DBCParseResult {
  if (!dbcContent || typeof dbcContent !== "string") {
    throw new Error("Invalid DBC content provided");
  }

  // Default to strict mode (case-sensitive, exact match)
  const parseOptions: ParseOptions = {
    caseSensitive: true,
    normalizeUnderscores: false,
    ...options,
  };

  const lines = dbcContent.split("\n");
  const signals: DBCSignal[] = [];
  const signalToMessages = new Map<string, Set<number>>();
  const messageInfo = new Map<number, string>(); // messageId -> messageName

  let currentMessageId: number | null = null;
  let currentMessageName: string | null = null;
  let messageCount = 0;
  let signalCount = 0;

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Skip empty lines and comments
    if (
      !trimmedLine ||
      trimmedLine.startsWith("//") ||
      trimmedLine.startsWith("#") ||
      trimmedLine.startsWith("/*") ||
      trimmedLine.startsWith("*/")
    ) {
      continue;
    }

    // Parse CAN message (BO_ line) - Rule 2.1
    // BO_ <id> <name> : <dlc> <transmitter>
    const messageMatch = trimmedLine.match(
      /^BO_\s+(\d+)\s+([^\s:]+)\s*:\s*\d+\s+\S+/
    );
    if (messageMatch) {
      currentMessageId = parseInt(messageMatch[1], 10);
      currentMessageName = messageMatch[2].trim();
      messageInfo.set(currentMessageId, currentMessageName);
      messageCount++;
      continue;
    }

    // Parse signal (SG_ line) - Rule 3.1-3.5
    // Only process if we're inside a message block
    if (currentMessageId !== null && currentMessageName !== null) {
      // SG_ <name> : <start_bit>|<length>@<byte_order><sign> (<min>,<max>) [<unit>] <receiver>
      // Also handle optional multiplex: SG_ <name> [M|m<number>] : ...
      const signalMatch = trimmedLine.match(
        /^\s*SG_\s+([A-Za-z_][A-Za-z0-9_]*)\s+(?:M|m\d+)?\s*:/
      );
      if (signalMatch) {
        const signalName = signalMatch[1].trim();

        // Skip empty signal names
        if (!signalName) {
          continue;
        }

        const signal: DBCSignal = {
          name: signalName,
          canId: currentMessageId,
          messageName: currentMessageName,
        };

        signals.push(signal);
        signalCount++;

        // Track signal occurrences for duplicate detection - Rule 5.1
        const normalizedName = normalizeSignalName(signalName, parseOptions);
        if (!signalToMessages.has(normalizedName)) {
          signalToMessages.set(normalizedName, new Set());
        }
        signalToMessages.get(normalizedName)!.add(currentMessageId);
      }
    }
  }

  // Validate that we found some content
  if (messageCount === 0) {
    throw new Error("No CAN messages found in DBC file");
  }

  if (signalCount === 0) {
    throw new Error("No signals found in DBC file");
  }

  // Find duplicates across different messages - Rule 5.4
  const duplicates: DuplicateSignalInfo[] = [];
  for (const [normalizedSignalName, messageIds] of signalToMessages.entries()) {
    if (messageIds.size > 1) {
      // Convert to array and sort by message ID - Rule 7.1
      const sortedMessageIds = Array.from(messageIds).sort((a, b) => a - b);

      // Find the original signal name (not normalized) for display
      const originalSignalName =
        signals.find(
          (s) =>
            normalizeSignalName(s.name, parseOptions) === normalizedSignalName
        )?.name || normalizedSignalName;

      duplicates.push({
        signalName: originalSignalName,
        appearsIn: sortedMessageIds.map((id) => ({
          id,
          name: messageInfo.get(id) || `Message_${id}`,
        })),
      });
    }
  }

  return {
    signals,
    duplicates,
    hasDuplicates: duplicates.length > 0,
  };
}

/**
 * Validates if a DBC file has duplicate signal names
 * @param dbcContent - The content of the DBC file as string
 * @param options - Parsing options for duplicate detection
 * @returns True if duplicates exist, false otherwise
 */
export function hasDuplicateSignals(
  dbcContent: string,
  options: ParseOptions = {}
): boolean {
  const result = parseDBCFile(dbcContent, options);
  return result.hasDuplicates;
}
